import React, { useState, useEffect, useCallback, useMemo } from "react";
import segmentData from "../utils/segmentData";
import "./ProductDrawer.css";

const ProductDrawer = ({
  isOpen,
  product,
  sizes,
  serviceType,
  onClose,
  onSizeChange,
  onAddToSelection,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const selectedSizes = useMemo(
    () => (Array.isArray(sizes) ? sizes : []),
    [sizes],
  );
  const lotDetails = useMemo(
    () => product?.lotDetails || [],
    [product?.lotDetails],
  );
  console.log(product);
  const moqLot = parseInt(product?.moqSet) || 1;
  const segment = product?.segment;
  const isCaratSegment =
    segment === "GEMS" ||
    segment === "PEARL" ||
    segment === "PEARLS" ||
    segment === "GEMSTONE" ||
    segment === "GEMSTONES";
  const isPriceOnlysegment =
    segment === "BRAND" ||
    segment === "TOOL" ||
    segment === "MACHINE" ||
    segment === "DECOR";
  const getAvailableSizes = useCallback(() => {
    const productName = product?.productName;
    const productSizes =
      segmentData[product?.segment]?.[product?.category]?.[productName]
        ?.options || [];
    const lotSizes = (product?.lotDetails || [])
      .map((lot) => lot.size)
      .filter(Boolean);
    const combined = Array.from(new Set([...productSizes, ...lotSizes]));
    if (combined.length === 0) {
      return ["Regular"];
    }
    return combined;
  }, [product]);

  // Get existing sizes from lotDetails (for highlighting)
  const getExistingSizes = useCallback(() => {
    return lotDetails.map((lot) => lot.size);
  }, [lotDetails]);

  const getAvailableQuantityForSize = useCallback(
    (size) => {
      const lotDetail = lotDetails.find((lot) => lot.size === size);
      return lotDetail ? parseInt(lotDetail.set) : 0;
    },
    [lotDetails],
  );

  const isSizeInLot = useCallback(
    (size) => {
      return getExistingSizes().includes(size);
    },
    [getExistingSizes],
  );

  // Auto-switch logic for Lot products preview
  // Keep the preview index valid when the selected product or its lots change.
  useEffect(() => {
    setCurrentIndex(0);
  }, [product?.id]);

  // Rotate previews only while the drawer is open and there is more than one lot.
  useEffect(() => {
    if (!isOpen || lotDetails.length < 2) return undefined;

    const interval = window.setInterval(() => {
      setCurrentIndex((previous) => (previous + 1) % lotDetails.length);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [isOpen, lotDetails.length, product?.id]);

  // Close drawer on Escape key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Initialize initial size selection when drawer opens
  useEffect(() => {
    if (!isOpen || !product) return;

    const availableSizesList = getAvailableSizes();
    const existingSizesList = getExistingSizes();

    const currentFirstSize = sizes?.[0]?.size;
    const isCurrentValid =
      currentFirstSize && availableSizesList.includes(currentFirstSize);

    if (
      !sizes ||
      sizes.length === 0 ||
      (!isCurrentValid &&
        currentFirstSize === "Regular" &&
        !availableSizesList.includes("Regular"))
    ) {
      const firstLotWithStock = lotDetails.find(
        (l) => parseInt(l.set) > 0,
      )?.size;
      const initialSize =
        firstLotWithStock ||
        existingSizesList[0] ||
        availableSizesList[0] ||
        "Regular";
      const availableQty = getAvailableQuantityForSize(initialSize);
      const isExisting = isSizeInLot(initialSize);

      onSizeChange([
        {
          size: initialSize,
          quantity: 1,
          availableQty: availableQty,
          isReadyStock: availableQty > 0,
          isExistingSize: isExisting,
        },
      ]);
    }
  }, [isOpen, product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getImageUrl = () => {
    if (!product?.images || product.images.length === 0) return "";
    if (product.images.length >= lotDetails.length && lotDetails.length > 0) {
      return (
        product.images[currentIndex]?.url?.trim() ||
        product.images[0]?.url?.trim() ||
        ""
      );
    } else {
      return product.images[0]?.url?.trim() || "";
    }
  };

  const getCurrentLotDetail = useCallback(() => {
    return lotDetails[currentIndex];
  }, [lotDetails, currentIndex]);

  const getDisplayQuantityForSize = (size) => {
    const lotDetail = lotDetails.find((lot) => lot.size === size);
    return lotDetail ? parseInt(lotDetail.set) : 0;
  };

  const updateQuantity = (index, delta) => {
    const newSizes = selectedSizes.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            quantity: Math.max(1, (Number(item.quantity) || 1) + delta),
          }
        : item,
    );
    onSizeChange(newSizes);
  };

  const handleQuantityInputChange = (index, value) => {
    // Keep the field usable while typing; normalize invalid/empty values on blur.
    const parsed = value === "" ? "" : Number.parseInt(value, 10);
    const newSizes = selectedSizes.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            quantity:
              parsed === ""
                ? ""
                : Math.max(1, Number.isFinite(parsed) ? parsed : 1),
          }
        : item,
    );
    onSizeChange(newSizes);
  };

  const normalizeQuantity = (index) => {
    const newSizes = selectedSizes.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            quantity: Math.max(1, Number.parseInt(item.quantity, 10) || 1),
          }
        : item,
    );
    onSizeChange(newSizes);
  };

  const handleSizeChange = (index, newSize) => {
    const newSizes = [...selectedSizes];
    const availableQty = getAvailableQuantityForSize(newSize);
    const isExisting = isSizeInLot(newSize);

    newSizes[index] = {
      ...newSizes[index],
      size: newSize,
      quantity: newSizes[index]?.quantity || 1,
      availableQty: availableQty,
      isReadyStock: availableQty > 0,
      isExistingSize: isExisting,
    };

    onSizeChange(newSizes);
  };

  const handleAddSize = () => {
    const availableSizesList = getAvailableSizes();
    const usedSizes = selectedSizes.map((s) => s.size);
    const availableNewSize = availableSizesList.find(
      (s) => !usedSizes.includes(s),
    );

    if (availableNewSize) {
      const availableQty = getAvailableQuantityForSize(availableNewSize);
      const isExisting = isSizeInLot(availableNewSize);
      const newSize = {
        size: availableNewSize,
        quantity: 1,
        availableQty: availableQty,
        isReadyStock: availableQty > 0,
        isExistingSize: isExisting,
      };
      onSizeChange([...selectedSizes, newSize]);
    }
  };

  const handleRemoveSize = (index) => {
    const newSizes = selectedSizes.filter((_, i) => i !== index);
    onSizeChange(newSizes);
  };

  const getTotalSelectedSets = () => {
    return selectedSizes.reduce(
      (total, size) => total + (Number.parseInt(size.quantity, 10) || 0),
      0,
    );
  };

  const validateMOQ = () => {
    const totalSets = getTotalSelectedSets();
    return totalSets >= moqLot;
  };

  const handleAddToSelection = () => {
    if (!validateMOQ()) {
      alert(
        `Minimum Order Quantity (MOQ) is ${moqLot} sets. Please select more items.`,
      );
      return;
    }
    const readyVariants = [];
    const orderVariants = [];

    selectedSizes.forEach((sizeConfig) => {
      const requestedQuantity = Math.max(
        1,
        Number.parseInt(sizeConfig.quantity, 10) || 1,
      );
      const availableQty = getAvailableQuantityForSize(sizeConfig.size);
      const readyQuantity = Math.min(requestedQuantity, availableQty);
      const orderQuantity = requestedQuantity - readyQuantity;
      const { availableQty: _, ...variant } = sizeConfig;

      if (readyQuantity > 0) {
        readyVariants.push({ ...variant, quantity: readyQuantity });
      }
      if (orderQuantity > 0) {
        orderVariants.push({ ...variant, quantity: orderQuantity });
      }
    });

    if (readyVariants.length > 0) {
      const readyProduct = {
        ...product,
        selectedVariants: readyVariants,
        serviceType: "ready",
      };
      onAddToSelection(readyProduct, "ready");
    }

    if (orderVariants.length > 0) {
      const orderProduct = {
        ...product,
        selectedVariants: orderVariants,
        serviceType: "order",
      };
      onAddToSelection(orderProduct, "order");
    }

    onClose();
  };

  const getWeightText = () => {
    const currentLot = getCurrentLotDetail();
    if (isCaratSegment) {
      return currentLot
        ? `${currentLot.avgNetWt} ct`
        : `${product.caratWt || ""} ct`;
    }
    return currentLot ? `${currentLot.avgNetWt}gm` : `${product.netWt}gm`;
  };

  const getSizeText = () => {
    const currentLot = getCurrentLotDetail();
    return currentLot ? `Size: ${currentLot.size}` : "Multiple Sizes";
  };

  // ─── PRICE-ONLY SEGMENT CALCULATION ───
  // PRICE + PRICE * operatorRatePercent/100 + PRICE * jmiRatePercent/100
  const getPriceOnlyAmount = useCallback(() => {
    const currentLot = getCurrentLotDetail();
    const num = (v) => parseFloat(v) || 0;
    const basePrice = num(currentLot?.price || 0);
    const operatorRatePercent = num(product?.operatorRatePercent || 0);
    const jmiRatePercent = num(product?.jmiRatePercent || 0);

    const calculatedPrice =
      basePrice +
      (basePrice * operatorRatePercent) / 100 +
      (basePrice * jmiRatePercent) / 100;

    const totalQty = selectedSizes.reduce(
      (sum, s) => sum + (parseFloat(s.quantity) || 1),
      0,
    );
    const qty = totalQty > 0 ? totalQty : 1;

    return calculatedPrice * qty;
  }, [
    getCurrentLotDetail,
    product?.operatorRatePercent,
    product?.jmiRatePercent,
    selectedSizes,
  ]);

  if (!isOpen || !product) return null;

  const totalwastage =
    parseFloat(product?.buyerDeskCalculation || 0) +
    parseFloat(product?.wastage || 0) +
    parseFloat(product?.wastageOperator || 0) +
    parseFloat(product?.wastageJmi || 0) +
    parseFloat(product?.specificationMC || 0) +
    (product?.specification && product.specification !== "Plane"
      ? (parseFloat(product?.specificationMC || 0) *
          parseFloat(product?.specificationMakingJmi || 0)) /
          100 +
        (parseFloat(product?.specificationMC || 0) *
          parseFloat(product?.specificationMakingOperator || 0)) /
          100
      : 0);

  // ─── CARAT WT (for carat segments) or Total Fine Wt (for others) ───
  const getTotalFinewt = () => {
    const num = (v) => parseFloat(v) || 0;
    // Original fine-weight logic for metal segments
    const wastageFactor = num(totalwastage) / 100;
    const totalFineWt = selectedSizes.reduce((total, size) => {
      const lot = lotDetails.find((l) => l.size === size.size);
      if (!lot) return total;
      const qty = num(size.quantity);
      const currentNetWt = num(lot.avgNetWt);
      return total + qty * currentNetWt * wastageFactor;
    }, 0);
    return totalFineWt.toFixed(3);
  };
  const getTotalCrtWt = () => {
    const num = (v) => parseFloat(v) || 0;
    const totalCaratWt = selectedSizes.reduce((total, size) => {
      const lot = lotDetails.find((l) => l.size === size.size);
      if (!lot) return total;
      const qty = num(size.quantity);
      const caratWt = num(lot.avgNetWt);
      return total + qty * caratWt;
    }, 0);
    return totalCaratWt.toFixed(3);
  };

  // ───  amount MC  ───
  const getTotalMC = () => {
    const num = (v) => parseFloat(v) || 0;

    // Original MC logic for other segments
    const setMcBase = num(product?.setMc);
    const setMc =
      setMcBase +
      setMcBase *
        ((num(product?.setMakingJmi) + num(product?.setMakingOperator)) / 100);

    const gramMcPerGram =
      num(product?.netGramMc) +
      num(product?.netGramMc) *
        ((num(product?.gramMakingJmi) + num(product?.gramMakingOperator)) /
          100);

    const specMcPerGram =
      num(product?.specificationGramRate) +
      num(product?.specificationGramRate) *
        ((num(product?.specificationGramRateJmi) +
          num(product?.specificationGramRateOperator)) /
          100);

    const totalProductMC = selectedSizes.reduce((total, size) => {
      const lot = lotDetails.find((l) => l.size === size.size);
      if (!lot) return total;
      const qty = num(size.quantity);
      const lotSetMc = setMc * qty;
      const lotGramMc = num(lot.avgNetWt) * gramMcPerGram * qty;
      const lotSpecMc = num(lot?.avgSpecWt) * specMcPerGram * qty;
      return total + lotSetMc + lotGramMc + lotSpecMc;
    }, 0);
    return totalProductMC.toFixed(2);
  };

  // ─── NEW: TOTAL AMOUNT for CARAT segments ───
  const caratRateTotal = () => {
    const num = (v) => parseFloat(v) || 0;
    const caratRateOperator = num(product?.maxCaratRateOperator);
    const caratRateJmi = num(product?.maxCaratRateJmi);

    const totalCaratAmount = selectedSizes.reduce((total, size) => {
      const lot = lotDetails.find((l) => l.size === size.size);
      if (!lot) return total;
      const qty = num(size.quantity);

      const caratRatePerLot =
        num(lot?.caratRate) +
        num(lot?.caratRate) * (caratRateJmi / 100) +
        num(lot?.caratRate) * (caratRateOperator / 100);
      console.log(lot?.caratRate, caratRateJmi, caratRateOperator);
      return total + caratRatePerLot * qty;
    }, 0);

    return totalCaratAmount.toFixed(2);
  };

  const availableSizes = getAvailableSizes();
  const existingSizes = getExistingSizes();
  const totalSelectedSets = getTotalSelectedSets();
  const isMOQValid = validateMOQ();

  return (
    <div
      className="bottom-drawer-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="bottom-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-drawer-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        {/* ── HEADER ── */}
        <div className="product-drawer-header">
          <div className="product-info">
            <div className="product-image-container">
              <img src={getImageUrl()} alt={product.productName || "Product"} />
              {lotDetails.length > 1 && (
                <div className="lot-progress-indicators">
                  {lotDetails.map((_, index) => (
                    <div
                      key={index}
                      className={`progress-dot ${
                        index === currentIndex ? "active" : ""
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="product-details-text">
              <h3 id="product-drawer-title">{product.productName}</h3>
              <p>
                {isCaratSegment ? "Carat Wt" : "Net Weight"} {getWeightText()}
              </p>
              <p className="size-info">{getSizeText()}</p>
              {serviceType === "order" ? (
                <div className="lot-info">
                  <span className="lot-badge">LOT</span>
                  <span className="moq-info">MOQ: {moqLot} sets</span>
                </div>
              ) : (
                <div className="lot-info">
                  <span className="lot-badge">LOT</span>
                  <span className="moq-info">MOQ: {moqLot} sets</span>
                  <span className="existing-sizes-info">
                    {existingSizes.length} sizes available
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* ── BODY ── */}
        <div className="drawer-body">
          <div className={`moq-validation ${isMOQValid ? "valid" : "invalid"}`}>
            <span>
              Selected: {totalSelectedSets} sets | MOQ: {moqLot} sets
              {!isMOQValid && (
                <span className="warning"> - Add more items</span>
              )}
            </span>
          </div>

          {selectedSizes.map((size, index) => {
            const availableQty = getAvailableQuantityForSize(size.size);
            const displayQty = getDisplayQuantityForSize(size.size);
            const isExistingSize = isSizeInLot(size.size);
            const exceedsStock = isExistingSize && size.quantity > availableQty;

            return (
              <div key={index} className="size-row">
                <div className="size-col">
                  <label>Size</label>
                  <select
                    value={size.size}
                    onChange={(e) => handleSizeChange(index, e.target.value)}
                    className={isExistingSize ? "existing-size" : "custom-size"}
                  >
                    <option value="" disabled>
                      Select Size
                    </option>
                    {availableSizes.map((s) => {
                      const isSizeExisting = isSizeInLot(s);
                      const isAlreadySelected = selectedSizes.some(
                        (sz) => sz.size === s && sz !== size,
                      );
                      const availableQuantity = getDisplayQuantityForSize(s);
                      return (
                        <option
                          key={s}
                          value={s}
                          disabled={isAlreadySelected}
                          className={
                            isSizeExisting ? "existing-option" : "custom-option"
                          }
                        >
                          {s}
                          {!isSizeExisting || availableQuantity === 0
                            ? serviceType === "order"
                              ? " (CUSTOM ORDER)"
                              : " (Custom order)"
                            : ` (${availableQuantity} available)`}
                        </option>
                      );
                    })}
                  </select>
                  <div className="size-status">
                    {isExistingSize && displayQty > 0 ? (
                      <span className="available-text">
                        ✓ Available in lot ({displayQty} sets)
                      </span>
                    ) : (
                      <span
                        className={`custom-text ${
                          serviceType === "order" ? "order-text" : ""
                        }`}
                      >
                        {serviceType === "order" ? "✓" : "✗"} Custom order only
                      </span>
                    )}
                  </div>
                </div>

                <div className="quantity-col">
                  <label>Quantity</label>
                  <div className="quantity-control">
                    <button
                      onClick={() => updateQuantity(index, -1)}
                      disabled={size.quantity <= 1}
                      className={`quantity-btn ${
                        serviceType === "order" ? "order-btn" : ""
                      }`}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      inputMode="numeric"
                      step="1"
                      value={size.quantity}
                      onChange={(e) =>
                        handleQuantityInputChange(index, e.target.value)
                      }
                      onBlur={() => normalizeQuantity(index)}
                      min="1"
                      aria-label={`Quantity for size ${size.size}`}
                      className={`${exceedsStock ? "exceeds-stock" : ""} ${
                        serviceType === "order" ? "order-quantity" : ""
                      }`}
                    />
                    <button
                      onClick={() => updateQuantity(index, 1)}
                      className={`quantity-btn ${
                        serviceType === "order" ? "order-btn" : ""
                      }`}
                    >
                      +
                    </button>
                  </div>
                  <div className="stock-info">
                    {!isExistingSize || availableQty === 0 ? (
                      <span className="custom-order-text">
                        This size will be custom ordered
                      </span>
                    ) : exceedsStock ? (
                      <span className="stock-warning">
                        Only {availableQty} available,{" "}
                        {size.quantity - availableQty} will be ordered
                      </span>
                    ) : (
                      <span className="stock-available">
                        {availableQty} available in stock
                      </span>
                    )}
                  </div>
                </div>

                {selectedSizes.length > 1 && (
                  <button
                    className="remove-size-btn"
                    onClick={() => handleRemoveSize(index)}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}

          {selectedSizes.length < availableSizes.length && (
            <button
              type="button"
              className="add-more-size"
              onClick={handleAddSize}
            >
              + Add More Size
            </button>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="drawer-footer">
          <div className="price-display">
            {isPriceOnlysegment ? (
              <p className="price-item total-amount">
                Price : ~₹{getPriceOnlyAmount().toFixed(2)}/-
              </p>
            ) : isCaratSegment ? (
              <>
                <p className="price-item">
                  Total Carat Wt : {getTotalCrtWt()} ct
                </p>
                <p className="price-item total-amount">
                  Amount : ~₹{caratRateTotal()}/-
                </p>
              </>
            ) : (
              <>
                <p className="price-item">Fine Wt : ~{getTotalFinewt()}/g</p>
                <p className="price-item">Amount : ~₹{getTotalMC()}/-</p>
              </>
            )}
          </div>

          <button
            type="button"
            className={`confirm-btn ${!isMOQValid ? "disabled" : ""}`}
            onClick={handleAddToSelection}
            disabled={!isMOQValid}
          >
            Add to Selection
          </button>
        </div>
      </section>
    </div>
  );
};

export default ProductDrawer;
